import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import SectionContainer from '@common/components/SectionContainer';
import { media, Row, Col } from 'styled-bootstrap-grid';
import { useTranslations } from '@lang';
import Image from 'next/image';
import { Body1 } from '@common/components/Typography';
import PaymentMethods from '@common/components/PaymentMethods';
import parsePropertyUrl from '@utils/parsePropertyUrl';
import { Link } from 'shared-routing';
import { Routes } from '@common/constants';
import getContrastFontColor from '@utils/getContrastFontColor';
import { SPACE_4 } from '@common/theme';

const StyledFooter = styled.footer`
  margin-top: auto; /* force footer to always be at the bottom of parrent flex container */
  width: 100%;
  font-family: 'Open Sans', sans-serif;
  color: ${({ theme }) =>
    getContrastFontColor(
      theme.navigationFooterColour || '#000',
      theme.defaultColors.fontPrimary,
      theme.defaultColors.lighterGrey
    )};
  font-size: 14px;

  a {
    color: ${({ theme }) =>
      getContrastFontColor(
        theme.navigationFooterColour || '#000',
        theme.defaultColors.fontPrimary,
        theme.defaultColors.lighterGrey
      )};

    &:hover {
      text-decoration: underline;
    }
  }
`;

const TopRow = styled.div`
  padding: 50px 0;
  background: ${({ theme }) => theme.navigationFooterColour || '#000'};
`;
const LogoSection = styled.div`
  position: absolute;
  right: 20px;
  display: flex;
  align-items: center;
`;

const PoweredBy = styled.span`
  color: #666666;
  letter-spacing: 0px;
  font-size: 14px;
`;

const ContactList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 25px;
  line-height: 24px;
`;

const BottomRow = styled.div`
  padding: 25px 0 50px;
  background: #fff;
  ${media.md`
    padding-bottom: 25px;
  `};
`;
const BottomFlexRow = styled.div`
  display: flex;
`;
const PaymentMethodsContainer = styled.div`
  display: flex;
  flex: 1 1 100%;
`;
const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 100%;
`;

const Footer = ({
  property = {},
  termsAndConditionPage = null,
  privacyAndCookiePolicyPage = null,
  cancellationPage = null,
}) => {
  const { t } = useTranslations();

  return (
    <StyledFooter>
      <TopRow>
        <SectionContainer>
          <Row>
            <Col xs="12" md="6" xl="3">
              <Body1 tag="h3" uppercase color="inherit" marginBottom={SPACE_4}>
                {t('homepage.footer.contact')}
              </Body1>
              <ContactList>
                {property.name && <li>{property.name}</li>}
                {property.address && <li>{property.address}</li>}
                {property.email && (
                  <li>
                    E: <a href={`mailto:${property.email}`}>{property.email}</a>
                  </li>
                )}
                {property.phoneNumber && (
                  <li>
                    T: <a href={`tel:${property.phoneNumber}`}>{property.phoneNumber}</a>
                  </li>
                )}
                {property.websiteUrl && (
                  <li>
                    <a href={parsePropertyUrl(property.websiteUrl)}>{property.websiteUrl}</a>
                  </li>
                )}
              </ContactList>
            </Col>
            {property.pages && property.pages.length > 0 && (
              <Col xs="12" md="6" xl="9">
                <Body1 tag="h3" uppercase color="inherit" marginBottom={SPACE_4}>
                  {t('homepage.footer.generalInformation')}
                </Body1>
                <ContactList>
                  {termsAndConditionPage && (
                    <li>
                      <Link route={Routes.TERMS_AND_CONDITIONS}>
                        {t('homepage.termsAndConditions')}
                      </Link>
                    </li>
                  )}
                  {privacyAndCookiePolicyPage && (
                    <li>
                      <Link route={Routes.PRIVACY_POLICY}>
                        {t('homepage.privacyAndCookiePolicy')}
                      </Link>
                    </li>
                  )}
                  {cancellationPage && (
                    <li>
                      <Link route={Routes.CANCELLATION_POLICY}>
                        {t('homepage.cancellationPolicy')}
                      </Link>
                    </li>
                  )}
                </ContactList>
              </Col>
            )}
          </Row>
        </SectionContainer>
      </TopRow>
      <BottomRow>
        <SectionContainer>
          <BottomFlexRow>
            <PaymentMethodsContainer>
              <PaymentMethods />
            </PaymentMethodsContainer>
            <LogoContainer>
              <LogoSection>
                <PoweredBy>Powered by &nbsp;</PoweredBy>
                <Image src="/static/journey-logo.svg" alt="Journey" width="92" height="20" />
              </LogoSection>
            </LogoContainer>
          </BottomFlexRow>
        </SectionContainer>
      </BottomRow>
    </StyledFooter>
  );
};

Footer.propTypes = {
  property: PropTypes.shape({
    name: PropTypes.string,
    address: PropTypes.string,
    email: PropTypes.string,
    phoneNumber: PropTypes.string,
    websiteUrl: PropTypes.string,
    pages: PropTypes.array,
  }),
  termsAndConditionPage: PropTypes.object,
  privacyAndCookiePolicyPage: PropTypes.object,
  cancellationPage: PropTypes.object,
};

export default Footer;
